import React, { Fragment, useEffect, useRef, useState } from "react";
import classNames from "classnames";
import axios, { CancelToken } from "axios";
import { Prompt, useHistory, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { IAPIResponse, StatusCode } from "api/protocols";

import { dequeue, enqueue, showError, showSuccess } from "redux/actions/ui";
import { IProduct } from "redux/reducers/models/product";
import { IOrder } from "redux/reducers/models/order";
import useLocalStorage from "hooks/useLocalStorage";
import useBarcodeScanner from "hooks/barCodeScanner/barCodeScanner";

import { ButtonNew as Button, ButtonNew } from "components/buttonNew";
import Input from "components/form/input/Input";
import Icon from "components/icon/Icon";
import {
  faArrowLeft,
  faArrowRight,
  faCreditCard,
  faEllipsisV,
  faRefresh,
  faShoppingCart,
  faUser,
  faUserEdit,
  faXmark,
} from "@fortawesome/pro-regular-svg-icons";
import { FolderMenu } from "components/folderMenu/FolderMenu";
import {
  clearStoredProductFolders,
  getStoredProductFolder,
  isStoredProductFolderFresh,
  upsertStoredProductFolder,
} from "helpers/productFoldersStorage";
import { IRegister, IRegisterGroup } from "../../Admin";

import "elements/register/register.scss";
import { GetVariant } from "api/rpc/2024-04/facilityAdmin/product/variant";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { loadDiscountOptions, loadPaymentOptions, setRegisterSheet } from "redux/actions/facility";
import { loadCart, postCart, postCartVariants } from "redux/actions/cart";
import CartMenuNew from "./CartMenuNew";
import ProductsNew from "./ProductsNew";
import { useRegisterContext } from "./RegisterContext";
import Spin from "components/spin/spin";
import Callout from "components/callout/Callout";
import { GetFolder, IFolder } from "api/rpc/2024-04/facilityAdmin/product/folder";
import { GetProduct } from "api/rpc/2024-04/facilityAdmin/product/product";
import { GetCart, GetRegisterTabs } from "api/rpc/2024-04/facilityAdmin/order/cart/cart";
import { GetOrder } from "api/rpc/2024-04/facilityAdmin/order/order";
import { TFunction } from "i18next";
import Sheet from "components/sheet/Sheet";
import { ICustomer } from "redux/reducers/models/customer";
import {
  GetCustomer,
  GetCustomerPaymentMethod,
  PostPaymentMethod,
  PostSetup,
} from "api/rpc/2024-04/facilityAdmin/customer/customer";
import SelectCustomer from "elements/register/SelectCustomer";
import { ICart, ICartLineItem } from "redux/reducers/models/cart";
import {
  SaveCartAsTab,
  PostMerge,
  PostCheckout,
  RemoveCardFromTab,
  PutCart,
  CloseTab,
} from "api/rpc/2024-04/facilityAdmin/cart/cart";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { displayCurrency } from "helpers/Helpers";
import MessageDisplay from "components/messageDisplay/MessageDisplay";
import { Badge } from "components/badge/Badge";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import moment from "moment";
import { applyInventoryQuantitiesToProducts, fetchTrackedInventoryQuantities } from "helpers/registerInventory";
import useCancelToken from "hooks/useCancelToken/useCancelToken";
import CombineTabsModal from "./combineTabsModal/CombineTabsModal";
import useOutstandingBalancePopup from "./outstandingBalancePopup/useOutstandingBalancePopup";
import Page from "components/page/Page";
import useModal from "hooks/modals/useModal";
import QuickChargeTabModal from "./quickChargeTabModal/QuickChargeTabModal";
import Portal from "elements/Portal";
import { ICustomerPaymentMethod } from "api/rpc/2024-04/customer/paymentMethod";
import { PostTransaction, PutCapture } from "api/rpc/2024-04/facilityAdmin/order/transaction";
import { useElements, useStripe } from "@stripe/react-stripe-js";
import Divider from "components/divider";
import CreditCardTab from "elements/teesheet/creditCard/CreditCardTab";
import { ICreditCard } from "pages/guest/Customer/CustomerProfile";
import { NavigationDropdownNew } from "components/navigationDropdownNew/NavigationDropdownNew";
import Popup from "components/popup/Popup";
import CardSection from "components/cardSection/CardSection";

import {
  StripeCardCvcElementChangeEvent,
  StripeCardExpiryElementChangeEvent,
  StripeCardNumberElementChangeEvent,
} from "@stripe/stripe-js";
import Toggle from "components/form/toggle/Toggle";
export interface IMainState {
  products: IProduct[];
  productsLoadingMessage: string;
  folders: IFolder[];
  selectedFolder: IFolder;
  unPaidOrder: IOrder;
  order: IOrder;
}

export interface IFilterState {
  search: string;
  barcodeSearch: boolean;
  sort: string;
  openNewTab: boolean;
  openViewTabs: boolean;
  newTabDescription: string;
  newTabCustomer: ICustomer;
  customerSearchQuery: string;
  customerSearchResults: (Partial<ICustomer> | ICustomer)[];
  currentCart: ICart;
  registerTabs: ICart[];
  showRegisterTabs: boolean;
  reloadRegisterTabs: boolean;
  tabSearch: string;
  updatedTab: ICart;
  openCombineTab: boolean;
  editingTab: boolean;
  combineSourceTab: ICart | null;
  customerPaymentMethods: ICustomerPaymentMethod[];
  selectedTabPaymentMethod: ICreditCard;
  confirmAuthorization: boolean;
  displayDetails: boolean;
  changeCustomerCard: boolean;
  selectPaymentMethod: boolean;
}
export interface ITerminalState {
  online: boolean;
  connecting: boolean;
  reader: any;
}

interface ILocationState {
  params: { cartId: string; orderId: number };
}

interface IQuickChargeModal {
  customer_id: number;
  customer_payment_methods: ICustomerPaymentMethod[];
  tab: ICart;
  manageCard: boolean;
  selectedPaymentMethodID: number | null;
  savedPaymentMethod: ICustomerPaymentMethod | null;
}

interface IRemoveCardModal {
  tab: ICart;
}

interface IAddNewCardModal {
  elementComplete: {
    cardNumber: boolean;
    cardExpiry: boolean;
    cardCvc: boolean;
  };
  saveCard: boolean;
  source: "filter" | "quick_charge";
}

function getRegisterTabItemCount(tab: ICart): number {
  if (typeof tab?.parent_line_item_count === "number") {
    return tab.parent_line_item_count;
  }

  return tab?.line_items?.filter(lineItem => !lineItem.parent_id).length ?? 0;
}

export default function RegisterNew() {
  const queryString = window.location.search;
  const orderId = new URLSearchParams(queryString).get("order_id");
  const token = new URLSearchParams(queryString).get("token");

  const location = useLocation();
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const dispatch = useAppDispatch();
  const { facilityStore, cartStore } = useAppSelector(store => store);
  const registerGroup = useLocalStorage<IRegisterGroup>("register_group");
  const defaultFolderId = useLocalStorage<number>("default_folder_id");
  const selectedRegister = useLocalStorage<IRegister>("register");
  const previousRegisterId = useRef<number>();
  const selectedFolderRef = useRef<IFolder>();
  const productsRef = useRef<IProduct[]>();
  const searchRef = useRef<string>();
  const { addLoadingVariant, removeLoadingVariant, updateRegisterState, registerState } = useRegisterContext();
  const history = useHistory();
  const { cancelToken, cancelRequest } = useCancelToken();
  const { checkOutstandingBalance, outstandingBalancePopup } = useOutstandingBalancePopup();

  const { state: removeCardModal, closeModal: closeRemoveCardModal } = useModal<IRemoveCardModal>({
    tab: null,
  });

  const {
    state: addNewCardModal,
    updateModal: updateAddNewCardModal,
    closeModal: closeAddNewCardModal,
  } = useModal<IAddNewCardModal>({
    elementComplete: {
      cardCvc: false,
      cardExpiry: false,
      cardNumber: false,
    },
    saveCard: false,
    source: "filter",
  });

  const {
    state: closeTabModal,
    updateModal: updateCloseTabModal,
    closeModal: closeCloseTabModal,
  } = useModal<{ tab: ICart }>({
    tab: null,
  });

  const [state, setState] = useState<IMainState>({
    products: null,
    productsLoadingMessage: null,
    folders: null,
    selectedFolder: null,
    unPaidOrder: null,
    order: null,
  });

  const [filterState, setFilterState] = useState<IFilterState>({
    search: "",
    barcodeSearch: false,
    sort: "alpha",
    openNewTab: false,
    openViewTabs: false,
    newTabDescription: "",
    newTabCustomer: null,
    customerSearchQuery: "",
    customerSearchResults: [null],
    currentCart: null,
    registerTabs: [],
    showRegisterTabs: false,
    reloadRegisterTabs: false,
    tabSearch: "",
    updatedTab: null,
    openCombineTab: false,
    editingTab: false,
    combineSourceTab: null,
    customerPaymentMethods: [],
    selectedTabPaymentMethod: null,
    confirmAuthorization: false,
    displayDetails: true,
    changeCustomerCard: false,
    selectPaymentMethod: false,
  });

  const {
    state: quickChargeModal,
    updateModal: setQuickChargeModal,
    closeModal: closeQuickChargeModal,
  } = useModal<IQuickChargeModal>({
    customer_id: null,
    tab: null,
    manageCard: false,
    customer_payment_methods: [],
    selectedPaymentMethodID: null,
    savedPaymentMethod: null,
  });

  selectedFolderRef.current = state.selectedFolder;
  productsRef.current = state.products;
  searchRef.current = filterState.search;

  const [combineTabsLoading, setCombineTabsLoading] = useState(false);
  const [registerTabsLoading, setRegisterTabsLoading] = useState(true);
  const [openingTabId, setOpeningTabId] = useState<number | null>(null);
  const openingTabRef = useRef(false);

  const handleBarcodeScan = async (scannedCode: string) => {
    const input = document.getElementById("select_customer_input_id");
    const isFocused = document.activeElement === input;

    if (isFocused === true) {
      return;
    }

    if (registerState?.barcodeScannerBlocked) {
      return;
    }

    if (!scannedCode.trim() || state?.order) {
      return;
    }

    try {
      updateMainState({ productsLoadingMessage: t("secure.facility.register.register_new.register_new.005") });

      const res = await GetVariant({ extended: true, barcode: scannedCode }, false);

      if (res.status === StatusCode.OK) {
        // Filter to only variants with facility access AND exact barcode match
        const variants = res.data.filter(
          variant =>
            (variant.facility_access === true && variant.barcode === scannedCode) || variant?.sku === scannedCode, // Exact barcode match
        );

        if (variants.length >= 1) {
          // Take the first matching variant (or the only one if length === 1)
          const variant = variants[0];
          void addLoadingVariant(variant, variant?.product);
          const updatedCart = await dispatch(
            postCartVariants(
              {
                cart_id: cartStore?.cart?.id,
                variants: [{ variant_id: variant?.id, quantity: 1, parent_id: null }],
              },
              false,
            ),
          );
          updateMainState({ productsLoadingMessage: null });
          void removeLoadingVariant(variant?.id);

          if (!updatedCart) {
            dispatch(showError(t("secure.facility.register.register_new.register_new.006")));
          } else {
            dispatch(showSuccess(t("Product added to cart")));

            if (variant?.product?.dynamic_price && updatedCart?.line_items) {
              const addedLineItem = updatedCart.line_items.find(
                (li: ICartLineItem) => li.variant_id === variant?.id && !li.parent_id,
              );
              if (addedLineItem) {
                updateRegisterState({ dynamicPriceLineItem: addedLineItem });
              }
            }
          }
        } else {
          updateMainState({ productsLoadingMessage: null });
          dispatch(showError(t("No variant found with this barcode")));
        }
      } else {
        updateMainState({ productsLoadingMessage: null });
        dispatch(showError(t("secure.facility.register.register_new.register_new.008")));
      }
    } catch (error) {
      console.error("Error in handleBarcodeScan:", error);
      updateMainState({ productsLoadingMessage: null });
      dispatch(showError(t("Error scanning barcode")));
    } finally {
      // Clear search after scan attempt
      updateFilterState({ search: "" });
    }
  };

  const { isScannerInput } = useBarcodeScanner(handleBarcodeScan, {
    interKeyDelay: 40, // Tune based on my scanner (30-50ms typically)
    maxTotalDuration: 2000,
    minLength: 6,
    enabled: !registerState?.barcodeScannerBlocked,
  });

  useEffect(() => {
    const shouldBlockUnload = !!state?.order && state.order.balance > 0 && state.order.financial_status !== "paid";

    //Show warning dialog box when browser is refreshed
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };

    if (shouldBlockUnload) {
      window.addEventListener("beforeunload", handleBeforeUnload);
    }

    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [state?.order, state?.order?.balance]);

  useEffect(() => {
    void loadRegisterCart();
    void checkSelectedRegister();
    void loadRegisterTabs();
    if (facilityStore?.paymentOptions?.length === 0) {
      void dispatch(loadPaymentOptions());
    }
    if (facilityStore?.discountOptions?.length === 0) {
      void dispatch(loadDiscountOptions());
    }
  }, []);

  useEffect(() => {
    const loadPaymentMethods = async () => {
      if (filterState?.newTabCustomer !== null) {
        const response = await loadCustomerPaymentMethods(filterState.newTabCustomer.id);

        const paymentMethods = response?.data ?? [];
        const savedOnTab = filterState.currentCart?.payment_method_saved_on_tab;
        const savedStillOnCustomer = savedOnTab?.customer_id === filterState.newTabCustomer.id;
        const savedInList = paymentMethods.find(method => method.id === savedOnTab?.id);
        const methods =
          savedStillOnCustomer && savedOnTab && !savedInList ? [savedOnTab, ...paymentMethods] : paymentMethods;

        setFilterState(prev => ({
          ...prev,
          customerPaymentMethods: methods,
          selectedTabPaymentMethod: savedStillOnCustomer ? savedInList ?? savedOnTab : null,
          displayDetails: true,
          changeCustomerCard: true,
          selectPaymentMethod: false,
        }));
      }
    };

    void loadPaymentMethods();
  }, [filterState?.newTabCustomer]);

  useEffect(() => {
    const source = axios.CancelToken.source();
    if (registerGroup) {
      void loadFolders(registerGroup.id, source.token);
    }
    return () => source.cancel();
  }, [registerGroup]);

  useEffect(() => {
    const nextRegisterId = selectedRegister?.id;
    if (!nextRegisterId) {
      return;
    }
    if (previousRegisterId.current === undefined) {
      previousRegisterId.current = nextRegisterId;
      return;
    }
    if (previousRegisterId.current === nextRegisterId) {
      return;
    }
    previousRegisterId.current = nextRegisterId;
    reapplyRegisterInventoryQuantities();
  }, [selectedRegister?.id]);

  useEffect(() => {
    if (filterState.reloadRegisterTabs) {
      void loadRegisterTabs();
      setFilterState(prevState => ({ ...prevState, reloadRegisterTabs: false }));
    }
  }, [filterState?.reloadRegisterTabs]);

  // Used for searching all products
  useEffect(() => {
    const source = axios.CancelToken.source();
    let mounted = true;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const search = () => {
      timeoutId = global.setTimeout(() => {
        void (() => {
          try {
            if (filterState.search === "") {
              if (mounted) {
                updateMainState({ products: undefined });
              }
              return;
            } else {
              if (mounted && !isScannerInput) {
                void loadProducts(source.token);
              }
            }
          } catch (error) {
            console.log("err", error);
          }
          return;
        })();
      }, 500);
    };

    search();
    return () => {
      mounted = false;
      clearTimeout(timeoutId);
      source.cancel();
    };
  }, [filterState.search, isScannerInput]);

  //Used for searching all customers to add to a tab
  useEffect(() => {
    const source = axios.CancelToken.source();
    let mounted = true;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    if (mounted === true) {
      timeoutId = global.setTimeout(() => {
        void searchForCustomers(mounted, filterState.customerSearchQuery, source.token);
      }, 300);
    }
    return () => {
      mounted = false;
      clearTimeout(timeoutId);
      setState(prevState => ({ ...prevState, customerSearchResults: [null] }));
      source.cancel("Cancelled");
    };
  }, [filterState.customerSearchQuery]);

  useEffect(() => {
    if (filterState?.registerTabs) {
      const tabs = [...filterState.registerTabs];

      filterState?.registerTabs?.map((tab: ICart, index: number) => {
        if (filterState?.updatedTab?.id === tab.id) {
          tabs.splice(index, 1);
          tabs.push(filterState?.updatedTab);
        }
      });

      setFilterState(prevState => ({ ...prevState, registerTabs: tabs.sort((a, b) => (a.id < b.id ? -1 : 1)) }));
    }
  }, [filterState?.updatedTab]);

  // Turn ON immediately when scanner burst is detected,
  // and turn OFF shortly after the burst stops (prevents flicker)
  useEffect(() => {
    if (isScannerInput && !filterState.barcodeSearch) {
      // Barcode scanning started, set barcodeSearch to true
      updateFilterState({ barcodeSearch: true });
    }

    if (!isScannerInput && filterState.barcodeSearch) {
      // Wait for a short debounce period to ensure scanning is complete
      const id = setTimeout(() => {
        updateFilterState({ barcodeSearch: false });
      }, 250); // Debounce to prevent flickering off too quickly

      return () => clearTimeout(id); // Clean up the timeout on each change
    }
  }, [isScannerInput, filterState.barcodeSearch, updateFilterState]); // Add updateFilterState to dependencies

  function checkSelectedRegister() {
    const register = JSON.parse(localStorage.getItem("register"));
    const registerId = register?.id ? register?.id : undefined;
    if (!registerId) {
      dispatch(setRegisterSheet(true));
    }
  }

  function updateMainState(newMainState: Partial<IMainState>) {
    setState((cur: IMainState) => {
      return { ...cur, ...newMainState };
    });
  }

  function updateFilterState(newFilterState: Partial<IFilterState>) {
    setFilterState(cur => {
      return { ...cur, ...newFilterState };
    });
  }

  function reapplyRegisterInventoryQuantities() {
    setState(cur => ({
      ...cur,
      products: cur.products ? [...cur.products] : cur.products,
      selectedFolder: cur.selectedFolder ? { ...cur.selectedFolder } : cur.selectedFolder,
    }));

    if (selectedFolderRef.current?.products?.length) {
      void loadInventoryLevels(selectedFolderRef.current, cancelToken());
    }
    if (searchRef.current && productsRef.current?.length) {
      void applyRegisterInventoryToSearchProducts(productsRef.current, cancelToken());
    }
  }

  async function fetchAndApplyRegisterInventoryToProducts(
    products: IProduct[],
    cancelToken: CancelToken,
  ): Promise<IProduct[] | undefined> {
    let productIds: Array<number> = [];
    let variantIds: Array<number> = [];
    products.forEach(product => {
      if (product?.track_inventory && product?.variants?.length > 0) {
        productIds = [...productIds, product.id];
        variantIds = [...variantIds, ...product.variants.map(variant => variant.id)];
      }
    });
    if (variantIds.length === 0) {
      return products;
    }

    const { status, getQuantity } = await fetchTrackedInventoryQuantities(variantIds, false, cancelToken);
    if (cancelToken?.reason) {
      return undefined;
    }
    if (status !== StatusCode.OK) {
      dispatch(showError(t("Error getting inventory details")));
      return products;
    }

    return applyInventoryQuantitiesToProducts(products, productIds, getQuantity);
  }

  async function applyRegisterInventoryToSearchProducts(products: IProduct[], cancelToken: CancelToken) {
    const updatedProducts = await fetchAndApplyRegisterInventoryToProducts(products, cancelToken);
    if (updatedProducts === undefined) {
      return;
    }
    updateMainState({ products: updatedProducts });
  }

  async function loadInventoryLevels(folder: IFolder, cancelToken: CancelToken) {
    const selectedFolder = { ...folder };
    const storedProductFolder = await getStoredProductFolder(selectedFolder?.id);
    if (!storedProductFolder?.products?.length) {
      return;
    }

    const updatedProducts = await fetchAndApplyRegisterInventoryToProducts(storedProductFolder.products, cancelToken);
    if (updatedProducts === undefined) {
      return;
    }

    const updatedFolder = { ...storedProductFolder, products: updatedProducts };
    await upsertStoredProductFolder(updatedFolder);
    updateMainState({ selectedFolder: updatedFolder });
  }

  async function loadFolders(registerGroupId: number, token?: CancelToken) {
    const folderRes = await GetFolder({ register_group_id: registerGroupId, extended: false }, false, token);

    if (token && token.reason) {
      return;
    }
    if (folderRes.status !== StatusCode.OK) {
      updateMainState({ folders: [] });
      dispatch(showError(t("secure.facility.register.register_new.register_new.001")));
      return;
    }

    const folders = folderRes.data;

    // Get default folder for the selected Register or the default set for the facility
    let selectedFolder: IFolder = undefined;

    if (defaultFolderId > 0) {
      selectedFolder = folders.find(folder => folder.id === defaultFolderId);
    } else {
      selectedFolder = folders.find(folder => folder.default === true);
    }

    if (!selectedFolder) {
      selectedFolder = folders?.[0];
    }

    updateMainState({ folders: folders });

    if (selectedFolder) {
      void handleChangeSelectedFolder(selectedFolder);
    }
  }

  async function loadProducts(cancelToken: CancelToken) {
    updateMainState({ productsLoadingMessage: t("secure.facility.register.register_new.register_new.002") });

    // Only do product search - NO barcode handling here anymore
    updateMainState({ products: undefined });
    const productRes = await GetProduct({ extended: true, search: filterState.search }, false, cancelToken);
    updateMainState({ productsLoadingMessage: null });

    if (productRes.status === StatusCode.OK) {
      const products = productRes.data ?? [];
      await applyRegisterInventoryToSearchProducts(products, cancelToken);
    } else {
      updateMainState({ products: [] });
      if (cancelToken && cancelToken?.reason) {
        updateMainState({ productsLoadingMessage: null });
        return;
      }
      dispatch(showError(t("secure.facility.register.register_new.register_new.003")));
    }
  }

  async function loadRegisterCart() {
    const locationState = location.state as ILocationState;
    if (locationState?.params) {
      dispatch(enqueue());
      //Get cart if location state cart token exists
      const cartRes = await GetCart({ id: Number(locationState?.params?.cartId) }, false);
      if (cartRes.status !== StatusCode.OK) {
        dispatch(showError(t("secure.facility.register.register_new.register_new.009")));
        dispatch(dequeue());
        return;
      }

      //Set the cart in redux without making another GET Cart call (tableCart used to avoid setting token in localStorage)
      void dispatch(loadCart({ tableCart: cartRes?.data[0] }, false));

      //Get the order so the user can continue to complete the order
      const orderRes = await GetOrder({ id: locationState?.params?.orderId, extended: true }, false);
      if (orderRes.status !== StatusCode.OK) {
        dispatch(showError(t("secure.facility.register.register_new.register_new.010")));
        dispatch(dequeue());
        return;
      }
      dispatch(dequeue());
      setState(prevState => ({ ...prevState, unPaidOrder: orderRes?.data[0] }));
    } else if (token && orderId) {
      dispatch(enqueue());
      //Get cart if location state cart token exists
      const cartRes = await GetCart({ token: token }, false);
      if (cartRes.status !== StatusCode.OK) {
        dispatch(showError(t("secure.facility.register.register_new.register_new.009")));
        dispatch(dequeue());
        return;
      }
      //Set the cart in redux without making another GET Cart call (tableCart used to avoid setting token in localStorage)
      void dispatch(loadCart({ tableCart: cartRes?.data[0] }, false));

      //Get the order so the user can continue to complete the order
      const orderRes = await GetOrder({ id: Number(orderId), extended: true }, false);
      if (orderRes.status !== StatusCode.OK) {
        dispatch(showError(t("secure.facility.register.register_new.register_new.010")));
        dispatch(dequeue());
        return;
      }
      dispatch(dequeue());
      setState(prevState => ({ ...prevState, unPaidOrder: orderRes?.data[0] }));
    } else {
      void dispatch(loadCart({ tableCart: null }, false));
      if (localStorage.getItem("register_cart_id")) {
        await dispatch(loadCart({ id: Number(localStorage.getItem("register_cart_id")) }, false));
      } else if (localStorage.getItem("register_cart_token")) {
        await dispatch(loadCart({ token: String(localStorage.getItem("register_cart_token")) }, false));
      } else {
        await dispatch(postCart(null, false));
      }
    }
  }

  async function handleChangeSelectedFolder(selectedFolder: IFolder) {
    if (selectedFolder?.id === undefined) {
      return;
    }
    void cancelRequest();

    const storedProductFolder = await getStoredProductFolder(selectedFolder.id);

    if (isStoredProductFolderFresh(selectedFolder, storedProductFolder)) {
      updateMainState({ selectedFolder: storedProductFolder });
      //Get updated product inventory
      if (storedProductFolder && storedProductFolder?.products && storedProductFolder?.products?.length > 0) {
        void loadInventoryLevels(storedProductFolder, cancelToken());
      }
    } else {
      const folderRes = await GetFolder({ id: selectedFolder.id, extended: true }, true);

      if (folderRes.status !== StatusCode.OK || folderRes.data?.length === undefined || folderRes.data.length === 0) {
        dispatch(showError(t("secure.facility.register.register_new.register_new.011")));
        return;
      }

      const newSelectedFolder = folderRes.data[0];
      await upsertStoredProductFolder(newSelectedFolder);

      updateMainState({ selectedFolder: newSelectedFolder });
      //Get updated product inventory
      if (newSelectedFolder && newSelectedFolder?.products && newSelectedFolder?.products?.length > 0) {
        void loadInventoryLevels(newSelectedFolder, cancelToken());
      }
    }
  }

  async function refreshFolderStorage() {
    await clearStoredProductFolders();

    if (state.selectedFolder) {
      void handleChangeSelectedFolder(state.selectedFolder);
    }

    dispatch(showSuccess(t("secure.facility.register.register_new.register_new.012")));
  }

  function renderProducts() {
    if (filterState.search && state.products) {
      const products = state.products?.sort((firstProd: IProduct, secondProd: IProduct) => {
        if (filterState.sort === "alpha") {
          if (firstProd.title < secondProd.title) {
            return -1;
          } else if (firstProd.title > secondProd.title) {
            return 1;
          } else {
            return 0;
          }
        }
      }) as [];
      return { useGridPositions: false, products };
    } else {
      const products = state.selectedFolder?.products ?? ([] as []);
      return { useGridPositions: true, products };
    }
  }

  async function loadRegisterTabs() {
    setRegisterTabsLoading(true);

    try {
      const res = await GetRegisterTabs(false);

      if (res.status !== StatusCode.OK) {
        dispatch(showError(t("Error loading register tabs")));
        return;
      }

      setFilterState(prevState => ({
        ...prevState,
        registerTabs: Array.isArray(res.data) ? res.data : [],
      }));
    } finally {
      setRegisterTabsLoading(false);
    }
  }

  async function setActiveRegisterTab(cart: ICart) {
    if (openingTabRef.current) {
      return;
    }

    let activeCart = cart;

    // Tab list payloads omit line items. Load the full cart only when it is opened.
    if (!cart?.line_items) {
      openingTabRef.current = true;
      setOpeningTabId(cart.id);

      try {
        const cartRes = await GetCart({ id: cart.id }, false);

        if (cartRes.status !== StatusCode.OK || !cartRes.data?.[0]) {
          dispatch(showError(t("Error loading tab")));
          return;
        }

        activeCart = cartRes.data[0];
      } finally {
        openingTabRef.current = false;
        setOpeningTabId(null);
      }
    }

    dispatch({
      type: "cart.update",
      payload: {
        isLoaded: true,
        cart: activeCart,
      },
    });

    localStorage.setItem("register_cart_id", String(activeCart.id));
    setFilterState(prevState => ({ ...prevState, showRegisterTabs: false }));
  }

  async function searchForCustomers(mounted: boolean, customerSearchQuery: string, cancelToken: CancelToken) {
    try {
      if (customerSearchQuery === "") {
        if (mounted) {
          setState(prevState => ({ ...prevState, customerSearchResults: [null], searchingCustomer: false }));
        }
        return;
      } else {
        setState(prevState => ({ ...prevState, searchingCustomer: true }));
        const res = await loadCustomers(customerSearchQuery, cancelToken);
        if (mounted) {
          setState(prevState => ({ ...prevState, customerSearchResults: res, searchingCustomer: false }));
        }
      }
    } catch (error) {
      console.error(error);
      setState(prevState => ({ ...prevState }));
    }
    return;
  }

  async function loadCustomers(customerSearchQuery: string, cancelToken: CancelToken) {
    const customerRes = await GetCustomer({ search: customerSearchQuery }, false, cancelToken);

    if (customerRes.status !== StatusCode.OK) {
      return [];
    }

    return customerRes.data as Array<Record<string, any>>;
  }

  const handleCustomerSelection = (customer: ICustomer) => {
    setFilterState(prevState => ({
      ...prevState,
      newTabCustomer: customer,
      selectedTabPaymentMethod: null,
      // changeCustomerCard: true,
    }));
  };

  function resetTabEditor() {
    closeAddNewCardModal();
    setFilterState(prevState => ({
      ...prevState,
      openNewTab: false,
      editingTab: false,
      currentCart: null,
      newTabCustomer: null,
      newTabDescription: "",
      selectedTabPaymentMethod: null,
      confirmAuthorization: false,
      customerPaymentMethods: null,
      changeCustomerCard: false,
      selectPaymentMethod: false,
      displayDetails: true,
    }));
  }

  function handleOpenEditTab(tab: ICart) {
    closeAddNewCardModal();
    setFilterState(prevState => ({
      ...prevState,
      openNewTab: true,
      editingTab: true,
      currentCart: tab,
      newTabDescription: tab.description ?? "",
      newTabCustomer: tab.customer ?? null,
      selectedTabPaymentMethod: tab.payment_method_saved_on_tab ?? null,
      customerPaymentMethods: [],
      displayDetails: true,
      selectPaymentMethod: false,
      confirmAuthorization: false,
      changeCustomerCard: false,
    }));
  }

  async function handleCreateRegisterTab(paymentMethodId?: number | null) {
    const editingTab = filterState.editingTab;
    const customerId = filterState?.newTabCustomer?.id ?? null;
    const params = {
      cart_id: filterState?.currentCart?.id,
      customer_id: customerId,
      description: filterState?.newTabDescription,
      customer_payment_method_id:
        paymentMethodId !== undefined
          ? paymentMethodId
          : customerId
          ? filterState?.selectedTabPaymentMethod?.id ?? null
          : null,
    };

    const tabRes = await SaveCartAsTab(params, true);

    if (tabRes.status !== StatusCode.OK) {
      dispatch(showError(editingTab ? t("Error updating tab") : t("Error saving cart as tab")));
      return;
    }

    const newTabCustomerId = filterState?.newTabCustomer?.id;
    const savedTab = tabRes.data as ICart;

    resetTabEditor();

    if (editingTab) {
      if (cartStore.cart?.id === savedTab.id) {
        dispatch({
          type: "cart.update",
          payload: {
            isLoaded: true,
            cart: savedTab,
          },
        });
      }
      void loadRegisterTabs();
      dispatch(showSuccess(t("Tab updated")));
    } else {
      void setActiveRegisterTab(savedTab);
      void loadRegisterTabs();
      dispatch(showSuccess(t("Successfully saved tab")));
    }

    if (newTabCustomerId) {
      void checkOutstandingBalance(newTabCustomerId);
    }
  }

  function handleTabUpdate(cart: ICart) {
    setFilterState(prevState => ({ ...prevState, updatedTab: cart }));
  }

  function handleOpenCombineTab(tab: ICart) {
    setFilterState(prevState => ({
      ...prevState,
      openCombineTab: true,
      combineSourceTab: tab,
    }));
  }

  function handleCloseCombineTab() {
    setFilterState(prevState => ({
      ...prevState,
      openCombineTab: false,
      combineSourceTab: null,
    }));
  }

  async function handleCombineTabs(sourceTabId: number) {
    const targetTab = filterState.combineSourceTab;
    if (!targetTab || !sourceTabId) {
      return;
    }

    setCombineTabsLoading(true);

    const mergeRes = await PostMerge(
      {
        cart_ids: [targetTab.id, sourceTabId],
      },
      true,
    );

    setCombineTabsLoading(false);

    if (mergeRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error combining tabs")));
      return;
    }

    const mergedCart = mergeRes.data as ICart;

    setFilterState(prevState => ({
      ...prevState,
      openCombineTab: false,
      combineSourceTab: null,
    }));

    if (cartStore.cart?.id === targetTab.id || cartStore.cart?.id === sourceTabId) {
      dispatch({
        type: "cart.update",
        payload: {
          isLoaded: true,
          cart: mergedCart,
        },
      });
      localStorage.setItem("register_cart_id", String(mergedCart.id));
    }

    void loadRegisterTabs();
    dispatch(showSuccess(t("Tabs combined successfully")));
  }

  function handleSelectPaymentMethod(paymentMethod: ICustomerPaymentMethod) {
    setFilterState(prev => ({
      ...prev,
      selectedTabPaymentMethod: prev.selectedTabPaymentMethod?.id !== paymentMethod.id ? paymentMethod : null,
      changeCustomerCard: false,
      displayDetails: true,
      selectPaymentMethod: false,
    }));
  }

  async function loadCustomerPaymentMethods(
    customer_id: number,
    paymentMethodId?: number,
  ): Promise<IAPIResponse<ICustomerPaymentMethod[]> | undefined> {
    const paymentMethodRes = await GetCustomerPaymentMethod({ customer_id: customer_id, id: paymentMethodId }, false);
    if (paymentMethodRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading customer payment methods")));
      return;
    }

    return paymentMethodRes;
  }

  function methodsForCharge(methods: ICustomerPaymentMethod[] = [], tab: ICart) {
    const saved = tab?.payment_method_saved_on_tab;
    if (saved && !methods.some(method => method.id === saved.id)) {
      return [saved, ...methods];
    }
    return methods;
  }

  async function openQuickChargeTab(tab: ICart, manageCard: boolean) {
    if (tab.customer) {
      const response = await loadCustomerPaymentMethods(tab.customer.id);
      if (response?.status !== StatusCode.OK) {
        dispatch(showError(t("Error loading payment methods.")));
        return;
      }

      setQuickChargeModal({
        customer_payment_methods: methodsForCharge(response.data, tab),
        tab: tab,
        customer_id: tab?.customer_id,
        isOpen: true,
        manageCard: manageCard,
        selectedPaymentMethodID: tab.payment_method_saved_on_tab?.id ?? null,
        savedPaymentMethod: tab.payment_method_saved_on_tab ?? null,
      });

      return;
    }

    setQuickChargeModal({
      tab: tab,
      customer_id: tab?.customer_id,
      isOpen: true,
      manageCard: manageCard,
      customer_payment_methods: methodsForCharge([], tab),
      selectedPaymentMethodID: tab.payment_method_saved_on_tab?.id ?? null,
      savedPaymentMethod: tab.payment_method_saved_on_tab ?? null,
    });
  }

  async function handleQuickCharge() {
    if (quickChargeModal?.manageCard) {
      void handleUpdateTabCard();
      return;
    } else {
      const tab = quickChargeModal?.tab;

      const paymentMethod =
        quickChargeModal?.customer_payment_methods?.find(
          method => method?.id === quickChargeModal?.selectedPaymentMethodID,
        ) ??
        (quickChargeModal?.savedPaymentMethod?.id === quickChargeModal?.selectedPaymentMethodID
          ? quickChargeModal.savedPaymentMethod
          : quickChargeModal?.tab?.payment_method_saved_on_tab?.id === quickChargeModal?.selectedPaymentMethodID
          ? quickChargeModal.tab.payment_method_saved_on_tab
          : null);

      const paymentOption = facilityStore?.paymentOptions?.find(
        option => option?.payment_method === paymentMethod?.type,
      );

      if (!tab || !paymentMethod || !paymentOption) {
        dispatch(showError(t("Please select a payment method")));
        return;
      }

      try {
        dispatch(enqueue());
        const register = JSON.parse(localStorage.getItem("register"));
        const registerId = register?.id;

        const checkoutRes = await PostCheckout(
          {
            cart_id: tab.id,
            register_id: registerId,
          },
          false,
        );

        if (checkoutRes?.status !== StatusCode.OK) {
          dispatch(showError(t("Error creating order")));
          return;
        }

        const order = checkoutRes.data.order;
        const orderId = order.id;

        const transactionRes = await PostTransaction(
          {
            kind: "sale",
            amount: Number(tab.total_price_due?.toFixed(2)),
            order_id: orderId,
            payment_option_id: paymentOption.id,
          },
          false,
        );

        if (transactionRes?.status !== StatusCode.OK) {
          dispatch(showError(t("Error processing transaction")));
          return;
        }

        const transaction = transactionRes.data;

        if (paymentOption.type === "card") {
          const clientSecret = transaction?.client_secret;
          const transactionId = transaction?.id;

          if (!clientSecret || !transactionId) {
            dispatch(showError(t("Unable to initialize card payment")));
            return;
          }

          const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
            payment_method: paymentMethod.stripe_payment_method_id,
          });

          if (stripeError) {
            dispatch(
              showError(
                stripeError.decline_code === "insufficient_funds"
                  ? t("Card declined due to insufficient funds.")
                  : stripeError.message,
              ),
            );
            return;
          }

          const captureRes = await PutCapture(
            {
              payment_intent_id: paymentIntent.id,
              transaction_id: transactionId,
            },
            false,
          );

          if (captureRes?.status !== StatusCode.OK) {
            dispatch(showError(t("Payment cannot be processed as it failed to capture the card.")));
            return;
          }
        }

        await loadRegisterTabs();
        dispatch(showSuccess(t("Transaction generated successfully")));

        void closeQuickChargeModal();
      } catch (error) {
        console.log(error);
        dispatch(showError(t("Error processing transaction")));
      } finally {
        dispatch(dequeue());
      }
    }
  }

  async function handleRemoveCardFromTab(tab: ICart) {
    const removeRes = await RemoveCardFromTab({ id: tab.id }, true);
    if (removeRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error removing card from tab.")));
    } else {
      dispatch(showSuccess(t("Payment method removed successfully")));
    }

    void closeRemoveCardModal();
    void loadRegisterTabs();
  }

  async function handleUpdateTabCard() {
    const res = await PutCart(
      {
        id: quickChargeModal?.tab.id,
        customer_payment_method_id: quickChargeModal?.selectedPaymentMethodID,
      },
      true,
    );

    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error updating payment method on tab")));
      return;
    }

    void closeQuickChargeModal();
    void loadRegisterTabs();
  }

  function handleSelectNewCard(payment_method_id: number) {
    setQuickChargeModal({ selectedPaymentMethodID: payment_method_id });
  }

  async function handleCloseTab() {
    const params = {
      cart_id: closeTabModal?.tab?.id,
    };

    const closeTabRes = await CloseTab(params, true);

    if (closeTabRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error closing tab")));
      return;
    }

    dispatch(showSuccess(t("Tab closed successfully")));

    void closeCloseTabModal();
    void loadRegisterTabs();
  }

  function handleCardSectionChange(
    e: StripeCardNumberElementChangeEvent | StripeCardExpiryElementChangeEvent | StripeCardCvcElementChangeEvent,
  ) {
    if (filterState?.selectedTabPaymentMethod !== undefined) {
      setFilterState(prev => ({ ...prev, selectedTabPaymentMethod: null }));
    }

    updateAddNewCardModal({
      elementComplete: { ...addNewCardModal.elementComplete, [e.elementType]: e.complete },
    });
  }

  const collectCard = async (cart_id: number, customer_id: number): Promise<ICustomerPaymentMethod | null> => {
    if (!stripe || !elements) {
      dispatch(showError(t("Error occurred before card could save. Please reattempt.")));
      return null;
    }

    // Loader for length of operation to stop user from clicking
    dispatch(enqueue());
    try {
      const paymentMethod = await stripe.createPaymentMethod({
        type: "card",
        card: elements.getElement("cardNumber"),
      });

      if (paymentMethod.paymentMethod) {
        const setupCardRes = await setupCard(paymentMethod.paymentMethod.id, cart_id, customer_id);
        const paymentMethodRes = await loadCustomerPaymentMethods(customer_id);

        if (paymentMethodRes?.data) {
          setFilterState(prev => ({
            ...prev,
            customerPaymentMethods: paymentMethodRes.data.some(
              (method: ICustomerPaymentMethod) => method.id === prev.selectedTabPaymentMethod?.id,
            )
              ? paymentMethodRes.data
              : prev.selectedTabPaymentMethod
              ? [prev.selectedTabPaymentMethod, ...paymentMethodRes.data]
              : paymentMethodRes.data,
          }));
        }

        dispatch(dequeue());
        return setupCardRes;
      } else {
        dispatch(dequeue());
        dispatch(showError(paymentMethod.error.message));
        return null;
      }
    } catch (e) {
      console.error(e);
      dispatch(dequeue());
    }

    dispatch(dequeue());
    return null;
  };

  const handleAddNewCard = async () => {
    const isFilter = addNewCardModal?.source === "filter";
    const cartId = isFilter ? filterState?.currentCart?.id : quickChargeModal?.tab.id;
    const customerId = isFilter ? filterState?.newTabCustomer?.id : quickChargeModal?.customer_id;

    const savedMethod = await collectCard(cartId, customerId);

    if (!savedMethod) {
      return;
    }

    closeAddNewCardModal();

    if (isFilter) {
      await handleCreateRegisterTab(savedMethod.id);
    }
  };

  const setupCard = async (
    payment_method: string,
    cart_id: number,
    customer_id: number,
  ): Promise<ICustomerPaymentMethod | null> => {
    if (!customer_id) {
      dispatch(showError(t("Select a customer before adding a card.")));
      return null;
    }

    const setupRes = await PostSetup({ customer_id }, true);

    if (setupRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error setting up payment method")));
      return null;
    }

    if (!payment_method || payment_method === "undefined") {
      return null;
    }

    const stripeRes = await stripe.confirmCardSetup(setupRes.data.setup_intent.client_secret as string, {
      payment_method: payment_method,
    });

    if (stripeRes.error) {
      dispatch(showError(stripeRes.error.message));
      return null;
    }

    const methodRes = await PostPaymentMethod({
      payment_method_id: stripeRes.setupIntent.payment_method,
      customer_id: customer_id,
      save_card: true,
      save_on_profile: addNewCardModal?.saveCard,
    });

    if (methodRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error saving payment method")));
      return null;
    }

    dispatch(showSuccess(methodRes.data.message as string));

    if (addNewCardModal?.source === "filter") {
      const savedMethod = methodRes.data as ICustomerPaymentMethod;
      setFilterState(prev => ({
        ...prev,
        selectedTabPaymentMethod: savedMethod,
        selectPaymentMethod: false,
        customerPaymentMethods: prev.customerPaymentMethods?.some(method => method.id === savedMethod.id)
          ? prev.customerPaymentMethods
          : [savedMethod, ...(prev.customerPaymentMethods ?? [])],
      }));
      return savedMethod;
    }

    const cartRes = await PutCart(
      {
        id: cart_id,
        customer_id,
        customer_payment_method_id: methodRes.data.id,
      },
      true,
    );

    if (cartRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error updating cart")));
      return null;
    }

    const response = await loadCustomerPaymentMethods(customer_id);
    if (response?.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading payment methods.")));
    }

    setQuickChargeModal({
      tab: cartRes?.data,
      selectedPaymentMethodID: cartRes?.data?.customer_payment_method_id,
      customer_payment_methods: methodsForCharge(response?.data ?? [], cartRes?.data as ICart),
      savedPaymentMethod: cartRes?.data?.payment_method_saved_on_tab ?? methodRes.data,
    });

    dispatch(showSuccess(t("Cart updated successfully")));
    return methodRes.data as ICustomerPaymentMethod;
  };

  return (
    <>
      <div
        className={classNames("ui-register", {
          "ui-register-tabs-overview": filterState?.showRegisterTabs,
        })}
      >
        {!registerGroup ? (
          <RegisterNotFound t={t} />
        ) : !state?.folders ? (
          <div style={{ display: "flex", height: "100%", margin: "auto" }}>
            <div className="text-center m-auto">
              <div style={{ height: "32px" }}>
                <Spin />
              </div>
              <div>{t("secure.facility.register.register_new.register_new.013")}</div>
            </div>
          </div>
        ) : (
          <>
            <div className="ui-register-folders_container">
              <FolderMenu
                options={state.folders}
                optionTitle="title"
                defaultSelected={state.folders.findIndex(folder => folder.id === state.selectedFolder?.id)}
                onClick={option => {
                  void handleChangeSelectedFolder(option as IFolder);
                }}
              />
            </div>

            {filterState?.showRegisterTabs ? (
              <div className="ui-register-new-products-container">
                <div className="ui-register-tabs-overview-container">
                  <div className="ui-register-tabs-overview-container_breadcrumb_container">
                    <div
                      className="ui-register-tabs-overview-container_breadcrumb_button_container"
                      onClick={() => setFilterState(prevState => ({ ...prevState, showRegisterTabs: false }))}
                    >
                      <FontAwesomeIcon
                        icon={faArrowLeft}
                        className="ui-register-tabs-overview-container_breadcrumb_button_container_icon"
                      />{" "}
                      <p className="ui-register-tabs-overview-container_breadcrumb_button_container_text">
                        {t("Back to register")}
                      </p>{" "}
                    </div>
                  </div>

                  <Page
                    className="ui-register-tabs-overview-page"
                    title={t("Saved tabs")}
                    subtitle={`${filterState?.registerTabs?.length} ${
                      filterState?.registerTabs?.length === 1 ? "item" : "items"
                    }`}
                    headerContent={
                      <div className="ui-register-tabs-overview-container_search">
                        <Input
                          id="tabSearchId"
                          value={filterState.tabSearch}
                          onChange={(value: any) => updateFilterState({ tabSearch: value.target.value })}
                          type="search"
                          placeholder={t("Search saved tabs...")}
                          autoFocus
                          labelHidden
                        />
                      </div>
                    }
                  >
                    <div className="ui-register-tabs-overview-container_tabs_container">
                      {registerTabsLoading && filterState.registerTabs.length === 0 ? (
                        <div className="text-center m-auto">
                          <Spin />
                        </div>
                      ) : null}
                      {filterState?.registerTabs
                        ?.filter(
                          tab =>
                            tab?.description
                              ?.toLocaleLowerCase()
                              .includes(filterState?.tabSearch.toLocaleLowerCase()) ||
                            tab?.customer?.full_name
                              ?.toLocaleLowerCase()
                              .includes(filterState?.tabSearch.toLocaleLowerCase()),
                        )
                        ?.map((tab: ICart, index: number) => {
                          return (
                            <div
                              className="ui-register-tabs-overview-container_tab_container"
                              key={index}
                              onClick={() => void setActiveRegisterTab(tab)}
                            >
                              <div className="flex justify-between items-center pb-3">
                                {tab.customer ? (
                                  <div className="ui-register-cart-new-customer-title-container">
                                    <FontAwesomeIcon icon={faUser} className="ui-register-cart-new-customer-icon" />
                                    <p className="ui-register-cart-new-customer-title">{tab?.customer?.full_name}</p>
                                  </div>
                                ) : (
                                  <p className="ui-register-tabs-overview-container_tab_title">{tab?.description}</p>
                                )}

                                {/* Dropdown? */}
                                <div
                                  className="ui-register-tabs-overview-container_actions"
                                  onClick={event => event.stopPropagation()}
                                >
                                  <div onClick={event => event.stopPropagation()}>
                                    {tab?.payment_method_saved_on_tab ? (
                                      <div
                                        className="ui-register-tabs-overview-container_icon_container"
                                        onClick={() => openQuickChargeTab(tab, false)}
                                      >
                                        <FontAwesomeIcon icon={faCreditCard} className="cursor-pointer" />
                                      </div>
                                    ) : null}
                                  </div>

                                  <NavigationDropdownNew
                                    showPlainTextLabel
                                    rightAlign
                                    label={
                                      <>
                                        <ButtonNew style={{ padding: 10 }} size="medium" type="secondary">
                                          <FontAwesomeIcon icon={faEllipsisV} />
                                        </ButtonNew>
                                      </>
                                    }
                                    sections={[
                                      [
                                        {
                                          label: t("Edit"),
                                          onClick: () => handleOpenEditTab(tab),
                                          icon: "pen",
                                        },
                                        {
                                          label: t("Combine Tab"),
                                          onClick: () => handleOpenCombineTab(tab),
                                          icon: "merge",
                                        },
                                      ],
                                      [
                                        {
                                          label: t("Delete"),
                                          onClick: () => updateCloseTabModal({ isOpen: true, tab: tab }),
                                          icon: "trash",
                                          color: "red",
                                        },
                                      ],
                                    ]}
                                  />
                                </div>
                              </div>

                              <hr />

                              <div className="flex justify-between items-center">
                                <div className="flex gap-2">
                                  <OverlayTrigger
                                    placement="bottom"
                                    overlay={
                                      <Tooltip id="leftMenuToolTip">
                                        {"Created by " +
                                          String(tab.admin_user?.full_name) +
                                          " on " +
                                          moment(tab.created_at as string).format("llll")}
                                      </Tooltip>
                                    }
                                  >
                                    <div>
                                      <Badge type="gray" size="medium">
                                        <div className="p-1 flex items-center gap-2">
                                          <FontAwesomeIcon
                                            icon={faUserEdit}
                                            className="ui-register-tabs-overview-container_tab_items_icon_user"
                                          />

                                          <p className="ui-register-tabs-overview-container_tab_items_text_user">
                                            {tab.admin_user?.initials}
                                          </p>
                                        </div>
                                      </Badge>
                                    </div>
                                  </OverlayTrigger>

                                  <Badge type="lightblue" size="medium">
                                    <div className="p-1 flex items-center gap-2">
                                      <FontAwesomeIcon
                                        icon={faShoppingCart}
                                        className="ui-register-tabs-overview-container_tab_items_icon"
                                      />

                                      <p className="ui-register-tabs-overview-container_tab_items_text">
                                        {getRegisterTabItemCount(tab)}
                                      </p>
                                    </div>
                                  </Badge>
                                </div>

                                <div className="ui-register-tabs-overview-container_tab_items_price">
                                  {displayCurrency("cad", tab.total_price)}
                                </div>

                                <div
                                  className="ui-register-tabs-overview-container_icon_container"
                                  onClick={() => void setActiveRegisterTab(tab)}
                                >
                                  {openingTabId === tab.id ? (
                                    <Spin />
                                  ) : (
                                    <FontAwesomeIcon icon={faArrowRight} className="cursor-pointer" />
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </Page>
                </div>
              </div>
            ) : (
              <>
                <div className="ui-register-new-products-container">
                  <div className="ui-register-new-products-content">
                    <div className="ui-register-top-bar">
                      <div className="flex justify-between mb-4 gap-4">
                        <div className="grow">
                          <Input
                            id="productSearchId"
                            value={filterState.search}
                            onChange={(value: any) => updateFilterState({ search: value.target.value })}
                            onFocus={() => updateFilterState({ barcodeSearch: false })}
                            type="search"
                            placeholder={t("secure.facility.register.register_new.register_new.017")}
                            autoFocus
                            labelHidden
                            trailingButtons={[
                              <Button key={2} type="secondary" onClick={refreshFolderStorage}>
                                <span className="mr-2">{t("Refresh")}</span>
                                <Icon icon={faRefresh} size="medium" />
                              </Button>,
                            ]}
                          />
                        </div>
                        <div
                          className="ui-register-tabs-button"
                          onClick={() => setFilterState(prevState => ({ ...prevState, showRegisterTabs: true }))}
                        >
                          <p className="ui-register-tabs-button_quantity">
                            {registerTabsLoading && filterState.registerTabs.length === 0
                              ? "…"
                              : filterState.registerTabs.length}
                          </p>
                          <p className="ui-register-tabs-button_text">{t("Saved tabs")}</p>
                        </div>
                      </div>
                    </div>

                    <div className={"product-content"}>
                      {state.products?.length === 0 && filterState.search && state.productsLoadingMessage === null ? (
                        <MessageDisplay
                          title={t("secure.facility.register.register_new.register_new.019")}
                          icon={"magnifying-glass"}
                          content={t("secure.facility.register.register_new.register_new.020")}
                        />
                      ) : state?.selectedFolder?.products?.length === 0 && !filterState.search ? (
                        <MessageDisplay
                          title={t("secure.facility.register.register_new.register_new.021")}
                          icon={"circle-exclamation"}
                          content={t("secure.facility.register.register_new.register_new.022")}
                          actions={[
                            {
                              label: t("secure.facility.register.register_new.register_new.023"),
                              action: () => history.push(`/admin/settings/folders/${state?.selectedFolder?.id}`),
                            },
                          ]}
                        />
                      ) : (
                        <ProductsNew
                          {...renderProducts()}
                          disabled={
                            !cartStore?.cart ||
                            cartStore?.cart?.status === "complete" ||
                            state?.unPaidOrder ||
                            state.order
                              ? true
                              : false
                          }
                          cart={cartStore?.cart}
                          selectedFolder={state.selectedFolder}
                          productsLoadingMessage={state?.productsLoadingMessage}
                          updateTabCart={handleTabUpdate}
                        />
                      )}
                    </div>
                  </div>
                </div>

                <div className="ui-register-cart-menu ui-register-cart-menu-new ">
                  <CartMenuNew
                    dataOrigin={{
                      setRegisterState: updateMainState,
                      setFilterState: updateFilterState,
                      unPaidOrder: state.unPaidOrder,
                      selectedFolder: state?.selectedFolder,
                      registerTabs: filterState.registerTabs,
                    }}
                  />
                </div>
              </>
            )}
          </>
        )}
      </div>
      <Prompt when={!!state?.order} message={() => `${t("secure.facility.register.register_new.register_new.014")}`} />
      <Portal isMounted={removeCardModal.isOpen}>
        <Popup
          open={removeCardModal?.isOpen}
          title={t("Remove Card from tab?")}
          onOk={() => handleRemoveCardFromTab(removeCardModal?.tab)}
        />
      </Portal>

      <Sheet
        size={"small"}
        closable
        title={filterState.editingTab ? t("Edit tab") : t("Save as tab")}
        supportText={
          filterState.editingTab
            ? t("Update the name, customer, or card on this tab.")
            : t("Save this tab for later checkout. Add a name or customer, and a card to charge later if needed.")
        }
        cancelText={t("Cancel")}
        open={filterState.openNewTab}
        okText={filterState.editingTab ? t("Save") : t("Save tab")}
        okDisabled={
          (filterState?.newTabDescription === "" && filterState?.newTabCustomer === null) ||
          (addNewCardModal.isOpen && addNewCardModal.source === "filter")
        }
        backDropCancel={false}
        height="flexible"
        onOk={() => handleCreateRegisterTab()}
        onCancel={resetTabEditor}
      >
        {filterState?.displayDetails && !filterState?.selectPaymentMethod ? (
          <>
            <div className="mt-3 mb-4">
              <Input
                id="newTabDescription"
                value={filterState?.newTabDescription}
                onChange={(value: any) =>
                  setFilterState(prevState => ({ ...prevState, newTabDescription: value.target.value }))
                }
                placeholder={t("Type in a name or brief description")}
                autoFocus
                label={t("Description")}
              />
            </div>

            <div className="new-tab-separator">
              <p className="new-tab-separator_text">{t("or")}</p>
            </div>

            <div>
              {filterState?.newTabCustomer ? (
                <div>
                  <p className="ui-register-new-tab-modal-customer-title">{t("Customer name")}</p>
                  <div className="ui-register-new-tab-modal-customer-container">
                    <div className="flex justify-center items-center">
                      <FontAwesomeIcon icon={faUser} className="ui-register-new-tab-modal-customer-icon" />
                    </div>

                    <div className="ui-register-new-tab-modal-customer-name-container">
                      <p className="ui-register-new-tab-modal-customer-name">
                        {filterState?.newTabCustomer?.full_name}
                      </p>
                      <FontAwesomeIcon
                        icon={faXmark}
                        className="ui-register-new-tab-modal-customer-name-icon"
                        onClick={() =>
                          setFilterState(prevState => ({
                            ...prevState,
                            newTabCustomer: null,
                            selectedTabPaymentMethod: null,
                          }))
                        }
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <SelectCustomer
                  onCustomerChange={customer => handleCustomerSelection(customer)}
                  permissionLevel={"facility"}
                  newCustomer
                  usePortal
                  label={t("Customer name")}
                />
              )}
              <p className="ui-register-new-tab-modal-customer-input-sub-text">
                {t("Only one customer can be added per tab.")}
              </p>{" "}
            </div>
            <Divider />
          </>
        ) : null}

        <div className="ui-register-tab-card-section">
          <h2 className="ui-register-tab-card-section_title">
            {t("Add card to this tab")} <span>{t("(Optional)")}</span>
          </h2>
          <p className="ui-register-tab-card-section_support">
            {t("Select a customer above to view saved cards and charge this tab later.")}
          </p>
        </div>

        {filterState?.newTabCustomer && addNewCardModal.isOpen && addNewCardModal.source === "filter" ? (
          <div className="ui-register-add-card-panel">
            <div className="ui-register-add-card-panel_header">
              <p>{t("Add new credit card")}</p>
              <div className="ui-register-add-card-panel_actions">
                <ButtonNew type="secondary" size="small" onClick={closeAddNewCardModal}>
                  {t("Cancel")}
                </ButtonNew>
                <ButtonNew type="primary" size="small" onClick={() => handleAddNewCard()}>
                  {t("Confirm")}
                </ButtonNew>
              </div>
            </div>
            <CardSection onChange={handleCardSectionChange} type="two-row" />
            <Toggle
              size="small"
              labelRight={t("Save card to customer profile")}
              checked={addNewCardModal.saveCard}
              onChange={event => updateAddNewCardModal({ saveCard: event.target.checked })}
            />
          </div>
        ) : filterState?.newTabCustomer ? (
          <>
            {filterState?.selectedTabPaymentMethod ? (
              <div className="ui-checkbox-group mt-3">
                <CreditCardTab
                  isSelected
                  card={filterState.selectedTabPaymentMethod}
                  onRemove={() =>
                    setFilterState(prev => ({
                      ...prev,
                      selectedTabPaymentMethod: null,
                    }))
                  }
                  setSelectedCard={() =>
                    setFilterState(prev => ({
                      ...prev,
                      selectPaymentMethod: true,
                    }))
                  }
                />
              </div>
            ) : null}

            {(!filterState?.selectedTabPaymentMethod || filterState?.selectPaymentMethod) &&
              filterState?.customerPaymentMethods?.map(paymentMethod => (
                <div className="ui-checkbox-group mt-3" key={paymentMethod.id}>
                  <CreditCardTab
                    card={paymentMethod}
                    isSelected={paymentMethod.id === filterState?.selectedTabPaymentMethod?.id}
                    setSelectedCard={() => handleSelectPaymentMethod(paymentMethod)}
                  />
                </div>
              ))}

            {!filterState?.selectedTabPaymentMethod && filterState?.customerPaymentMethods?.length === 0 ? (
              <div className="mt-3">
                <Callout
                  type="info"
                  title={t("No saved payment method")}
                  content={t("{{name}} doesn’t have any saved payment methods yet. Create one to charge this tab.", {
                    name: filterState.newTabCustomer.full_name,
                  })}
                  actions={{
                    primary: {
                      label: t("Add new card"),
                      buttonType: "secondary",
                      onClick: () => updateAddNewCardModal({ isOpen: true, source: "filter" }),
                    },
                  }}
                />
              </div>
            ) : (
              <ButtonNew type="text" onClick={() => updateAddNewCardModal({ isOpen: true, source: "filter" })}>
                {t("Add new card")}
              </ButtonNew>
            )}
          </>
        ) : null}
      </Sheet>

      <Portal isMounted={filterState?.openCombineTab}>
        <CombineTabsModal
          open={filterState.openCombineTab}
          targetTab={filterState.combineSourceTab}
          availableTabs={filterState.registerTabs}
          onCancel={handleCloseCombineTab}
          onOk={sourceTabId => void handleCombineTabs(sourceTabId)}
          loading={combineTabsLoading}
        />
      </Portal>

      <Portal isMounted={quickChargeModal?.isOpen}>
        <QuickChargeTabModal
          open={quickChargeModal?.isOpen}
          customerPaymentMethods={quickChargeModal.customer_payment_methods}
          selectedCardID={quickChargeModal.selectedPaymentMethodID}
          onOk={handleQuickCharge}
          manageCard={quickChargeModal?.manageCard}
          setSelectedCard={(id: number) => handleSelectNewCard(id)}
          onCancel={closeQuickChargeModal}
          tab={quickChargeModal?.tab}
          onAddCard={() => {
            if (!quickChargeModal?.customer_id) {
              dispatch(showError(t("Select a customer before adding a card.")));
              return;
            }
            updateAddNewCardModal({
              isOpen: true,
              source: "quick_charge",
            });
          }}
        />
      </Portal>

      <Portal isMounted={closeTabModal?.isOpen}>
        <Popup
          open={closeTabModal?.isOpen}
          type="warning"
          title={t("Delete Tab?")}
          description={t(
            "Closing a tab before checkout will delete all items and can't be undone. Would you like to continue?",
          )}
          onOk={handleCloseTab}
          okText={t("Yes, delete tab")}
          cancelText={t("Cancel")}
          onCancel={closeCloseTabModal}
          backDropCancel={true}
        />
      </Portal>

      <Portal isMounted={addNewCardModal.isOpen && addNewCardModal.source === "quick_charge"}>
        <Sheet
          title={t("Add new card")}
          open={addNewCardModal.isOpen}
          size={"small"}
          stacked
          okText={t("Confirm")}
          onOk={() => handleAddNewCard()}
          onCancel={closeAddNewCardModal}
        >
          <div className="flex flex-column">
            <CardSection onChange={handleCardSectionChange} type="two-row" />
            <div className="mt-2">
              <Toggle
                size="small"
                labelRight={t("Save card to customer profile")}
                checked={addNewCardModal.saveCard}
                onChange={event => updateAddNewCardModal({ saveCard: event.target.checked })}
              />
            </div>
          </div>
        </Sheet>
      </Portal>
      {outstandingBalancePopup}
    </>
  );
}

export function RegisterNotFound(props: { t: TFunction }) {
  return (
    <div style={{ width: "100%" }}>
      <Callout
        title={props.t("secure.facility.register.register_new.register_new.015")}
        content={props.t("secure.facility.register.register_new.register_new.016")}
        type="error"
        actions={{
          primary: {
            label: props.t("secure.facility.register.register_new.register_new.018"),
            onClick: () => window.open("https://academy.teeon.com/academy-cloud/how-to-change-the-register-"),
          },
        }}
      />
    </div>
  );
}
